/*
 * Scroll-reveal engine: elements matching REVEAL_SELECTORS start slightly
 * translated + transparent and settle into place when they enter the
 * viewport. Sibling cards in the same grid reveal with a small stagger.
 *
 * A MutationObserver keeps it working across route changes (pages are
 * lazy-loaded) without every component needing to know about it.
 * If JavaScript never runs, no classes are added and everything stays
 * fully visible.
 */

const REVEAL_SELECTORS = [
  '.introduction-container',
  '.Intro2nd-container',
  '.gallery-container > h3',
  '.gallery-container > .separator',
  '.Luxgallery-container > h3',
  '.Luxgallery-container > .separator',
  '.feature-container > h3',
  '.feature-container > .separator',
  '.feature-container .feature',
  '.category-card',
  '.guest-reviews > h3',
  '.guest-reviews > .separator',
  '.guest-reviews-rating',
  '.guest-review-card',
  '.guest-reviews-cta',
  '.all-suites-note',
  '.resto-section',
  '.villa-stat',
  '.villa-story-text',
  '.villa-mosaic-wrap > h3',
  '.villa-quote-inner',
];

/* Card-like elements get a stagger so grids cascade instead of popping
   in as one block. */
const STAGGER_SELECTORS = [
  '.feature-container .feature',
  '.category-card',
  '.guest-review-card',
  '.villa-stat',
];
const STAGGER_STEP_MS = 90;
const STAGGER_MAX_MS = 450;

const JOINED = REVEAL_SELECTORS.join(',');

let started = false;

export function initReveal() {
  if (started) return;
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  started = true;

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const el = entry.target;
          el.classList.add('reveal-in');
          io.unobserve(el);
          // Drop the stagger delay once revealed so hover transitions
          // on the same element respond instantly.
          if (el.style.transitionDelay) {
            setTimeout(() => {
              el.style.transitionDelay = '';
            }, 1500);
          }
        }
      }
    },
    { rootMargin: '0px 0px -60px 0px', threshold: 0.05 }
  );

  const register = (el) => {
    if (el.classList.contains('reveal')) return;
    el.classList.add('reveal');
    for (const sel of STAGGER_SELECTORS) {
      if (el.matches(sel)) {
        const siblings = el.parentElement ? [...el.parentElement.children] : [el];
        const idx = siblings.indexOf(el);
        el.style.transitionDelay = `${Math.min(idx * STAGGER_STEP_MS, STAGGER_MAX_MS)}ms`;
        break;
      }
    }
    io.observe(el);
  };

  const scan = (root) => {
    if (root.nodeType !== 1) return;
    if (root.matches && root.matches(JOINED)) register(root);
    if (root.querySelectorAll) root.querySelectorAll(JOINED).forEach(register);
  };

  scan(document.body);

  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach(scan);
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });
}
